import { ApolloLink, Observable } from '@apollo/client/core';
import { hasDirectives, getMainDefinition, getFragmentDefinitions, createFragmentMap, addTypenameToDocument, isField, isInlineFragment, resultKeyNameFromField, checkDocument, removeDirectivesFromDocument, } from '@apollo/client/utilities';
import * as qs from 'qs';
import { graphql } from './utils/graphql';
const isNullish = (value) => value === null || value === undefined;
const popOneSetOfArrayBracketsFromTypeName = (typename) => {
    const noSpace = typename.replace(/\s/g, '');
    const sansOneBracketPair = noSpace.replace(/\[(.*)\]/, (str, matchStr) => {
        return ((!isNullish(matchStr) && matchStr.length) > 0 ? matchStr : null) || noSpace;
    });
    return sansOneBracketPair;
};
const addTypeNameToResult = (result, __typename, typePatcher, typePatcherContext) => {
    if (Array.isArray(result)) {
        const fixedTypename = popOneSetOfArrayBracketsFromTypeName(__typename);
        // Recursion needed for multi-dimensional arrays
        return result.map(e => addTypeNameToResult(e, fixedTypename, typePatcher, typePatcherContext));
    }
    if (isNullish(result) || typeof result === 'number' || typeof result === 'boolean' || typeof result === 'string') {
        return result;
    }
    return typePatcher(result, __typename, typePatcher, typePatcherContext);
};
/**
 * Prefix used for every `__typename` that RestLink synthesises automatically for an
 * `@rest(...)` call that did not declare a `type`. It is deliberately unlikely to collide
 * with a real GraphQL type name so consumers (e.g. the InMemoryCache `dataIdFromObject`)
 * can recognise these synthetic types and keep them embedded inline rather than normalising
 * them into their own cache entries.
 */
export const AUTOMATIC_TYPENAME_PREFIX = 'RestLinkAuto';
const capitalize = (value) => (value.length === 0 ? value : value[0].toUpperCase() + value.slice(1));
/**
 * Builds a deterministic synthetic `__typename` from the response-key path of a node inside a
 * REST result (e.g. `['fetchThings', 'owner']` -> `RestLinkAuto_FetchThings_Owner`). Determinism
 * matters so that re-fetching the same query produces the same `__typename` at every level, which
 * keeps cache writes updating in place.
 */
const buildAutomaticTypeName = (path) => [AUTOMATIC_TYPENAME_PREFIX, ...path.map(capitalize)].join('_');
/**
 * Flattens a selection set into its concrete field nodes, descending through inline fragments and
 * named fragment spreads so their fields are treated as if selected directly on the parent object.
 */
const collectFieldSelections = (selectionSet, fragmentMap) => {
    if (isNullish(selectionSet)) {
        return [];
    }
    return selectionSet.selections.flatMap(node => {
        if (isField(node)) {
            return [node];
        }
        if (isInlineFragment(node)) {
            return collectFieldSelections(node.selectionSet, fragmentMap);
        }
        const fragment = fragmentMap[node.name.value];
        return isNullish(fragment) ? [] : collectFieldSelections(fragment.selectionSet, fragmentMap);
    });
};
/**
 * Recursively injects a synthetic `__typename` into every object node of a REST result, walking the
 * result in parallel with the query selection set. This is used for `@rest(...)` calls that omit
 * `type`, so that callers no longer have to declare a `type` (and matching `typePatcher`) purely to
 * satisfy Apollo's `__typename` selections. Existing `__typename` values are preserved.
 *
 * @param current The current node in the REST result being patched.
 * @param selectionSet The selection set of the query that produced `current`.
 * @param fragmentMap Map of named fragments used to resolve fragment spreads.
 * @param path The response-key path from the `@rest` field down to `current`, used to derive names.
 * @returns The same node, mutated in place with synthetic `__typename`s.
 */
const addAutomaticTypeNames = (current, selectionSet, fragmentMap, path) => {
    if (isNullish(current) || typeof current !== 'object') {
        return current;
    }
    if (Array.isArray(current)) {
        current.forEach(item => addAutomaticTypeNames(item, selectionSet, fragmentMap, path));
        return current;
    }
    if (isNullish(current.__typename)) {
        current.__typename = buildAutomaticTypeName(path);
    }
    collectFieldSelections(selectionSet, fragmentMap).forEach(field => {
        if (isNullish(field.selectionSet) || field.name.value === '__typename') {
            return;
        }
        // Index the raw REST payload by the real field name: aliasing (`resultKeyNameFromField`) is
        // only applied later by the executor, so at this point the payload keys are the field names.
        const fieldName = field.name.value;
        const value = current[fieldName];
        if (!isNullish(value) && typeof value === 'object') {
            addAutomaticTypeNames(value, field.selectionSet, fragmentMap, [...path, fieldName]);
        }
    });
    return current;
};
const quickFindRestDirective = (field) => {
    var _a;
    if (field.directives && field.directives.length) {
        return (_a = field.directives.find(directive => directive.name.value === 'rest')) !== null && _a !== void 0 ? _a : null;
    }
    return null;
};
/**
 * The way graphql works today, it doesn't hand us the AST tree for our query, it hands us the ROOT
 * This method searches for REST-directive-attached nodes that are named to match this query.
 *
 * A little bit of wasted compute, but alternative would be a patch in graphql-anywhere.
 *
 * @param resultKey SearchKey for REST directive-attached item matching this sub-query
 * @param current current node in the REST-JSON-response
 * @param mainDefinition Parsed Query Definition
 * @param fragmentMap Map of Named Fragments
 * @param currentSelectionSet Current selection set we're filtering by
 */
function findRestDirectivesThenInsertNullsForOmittedFields(resultKey, current, // currentSelectionSet starts at root, so wait until we're inside a Field tagged with an @rest directive to activate!
mainDefinition, fragmentMap, currentSelectionSet) {
    if (isNullish(currentSelectionSet) ||
        isNullish(current) ||
        typeof current === 'number' ||
        typeof current === 'boolean' ||
        typeof current === 'string') {
        return current;
    }
    currentSelectionSet.selections.forEach(node => {
        if (isInlineFragment(node)) {
            findRestDirectivesThenInsertNullsForOmittedFields(resultKey, current, mainDefinition, fragmentMap, node.selectionSet);
        }
        else if (node.kind === 'FragmentSpread') {
            const fragment = fragmentMap[node.name.value];
            findRestDirectivesThenInsertNullsForOmittedFields(resultKey, current, mainDefinition, fragmentMap, fragment.selectionSet);
        }
        else if (isField(node)) {
            const name = resultKeyNameFromField(node);
            if (name === resultKey && !isNullish(quickFindRestDirective(node))) {
                // Jackpot! We found our selectionSet!
                insertNullsForAnyOmittedFields(current, mainDefinition, fragmentMap, node.selectionSet);
            }
            else {
                findRestDirectivesThenInsertNullsForOmittedFields(resultKey, current, mainDefinition, fragmentMap, node.selectionSet);
            }
        }
        else {
            // This will give a TypeScript build-time error if you did something wrong or the AST changes!
            return ((_node) => {
                throw new Error('Unhandled Node Type in SelectionSetNode.selections');
            })(node);
        }
    });
    // Return current to have our result pass to next link in async promise chain!
    return current;
}
/**
 * Recursively walks a handed object in parallel with the Query SelectionSet,
 *  and inserts `null` for any field that is missing from the response.
 *
 * This is needed because ApolloClient will throw an error automatically if it's
 *  missing -- effectively making all of rest-link's selections implicitly non-optional.
 *
 * If you want to implement required fields, you need to use typePatcher to *delete*
 *  fields when they're null and you want the query to fail instead.
 *
 * @param current Current object we're patching
 * @param mainDefinition Parsed Query Definition
 * @param fragmentMap Map of Named Fragments
 * @param currentSelectionSet Current selection set we're filtering by
 */
function insertNullsForAnyOmittedFields(current, // currentSelectionSet starts at root, so wait until we're inside a Field tagged with an @rest directive to activate!
mainDefinition, fragmentMap, currentSelectionSet) {
    if (isNullish(currentSelectionSet) ||
        isNullish(current) ||
        typeof current === 'number' ||
        typeof current === 'boolean' ||
        typeof current === 'string') {
        return;
    }
    if (Array.isArray(current)) {
        // If our current value is an array, process our selection set for each entry.
        current.forEach(c => insertNullsForAnyOmittedFields(c, mainDefinition, fragmentMap, currentSelectionSet));
        return;
    }
    currentSelectionSet.selections.forEach(node => {
        if (isInlineFragment(node)) {
            insertNullsForAnyOmittedFields(current, mainDefinition, fragmentMap, node.selectionSet);
        }
        else if (node.kind === 'FragmentSpread') {
            const fragment = fragmentMap[node.name.value];
            insertNullsForAnyOmittedFields(current, mainDefinition, fragmentMap, fragment.selectionSet);
        }
        else if (isField(node)) {
            const value = current[node.name.value];
            if (node.name.value === '__typename') {
                // Don't mess with special fields like __typename
            }
            else if (typeof value === 'undefined') {
                // Patch in a null where the field would have been marked as missing
                current[node.name.value] = null;
            }
            else if (!isNullish(value) && typeof value === 'object' && !isNullish(node.selectionSet)) {
                insertNullsForAnyOmittedFields(value, mainDefinition, fragmentMap, node.selectionSet);
            }
            else {
                // Other types (string, number) do not need recursive patching!
            }
        }
        else {
            // This will give a TypeScript build-time error if you did something wrong or the AST changes!
            return ((_node) => {
                throw new Error('Unhandled Node Type in SelectionSetNode.selections');
            })(node);
        }
    });
}
const getEndpointOptions = (endpoints, endpoint) => {
    const result = endpoints[endpoint || DEFAULT_ENDPOINT_KEY] || endpoints[DEFAULT_ENDPOINT_KEY];
    if (typeof result === 'string') {
        return { uri: result };
    }
    return {
        responseTransformer: null,
        ...result,
    };
};
/** Replaces params in the path, keyed by colons */
const replaceLegacyParam = (endpoint, name, value) => {
    if (value === undefined || name === undefined) {
        return endpoint;
    }
    return endpoint.replace(`:${name}`, value);
};
/** Internal Tool that Parses Paths for RestLink -- This API should be considered experimental */
export class PathBuilder {
    static replacerForPath(path) {
        if (path in PathBuilder.cache) {
            return PathBuilder.cache[path];
        }
        const queryOrigStartIndex = path.indexOf('?');
        const pathBits = path.split(PathBuilder.argReplacement);
        const chunkActions = [];
        let hasBegunQuery = false;
        pathBits.reduce((processedCount, bit) => {
            if (bit === '' || bit === '{}') {
                // Empty chunk, do nothing
                return processedCount + bit.length;
            }
            const nextIndex = processedCount + bit.length;
            if (bit[0] === '{' && bit[bit.length - 1] === '}') {
                // Replace some args!
                const _keyPath = bit.slice(1, bit.length - 1).split('.');
                chunkActions.push((props, useQSEncoder) => {
                    try {
                        const value = PathBuilderLookupValue(props, _keyPath);
                        if (!useQSEncoder || typeof value !== 'object' || isNullish(value)) {
                            return String(value);
                        }
                        else {
                            return qs.stringify(value);
                        }
                    }
                    catch (e) {
                        const key = [path, _keyPath.join('.')].join('|');
                        if (!(key in PathBuilder.warnTable)) {
                            console.warn('Warning: RestLink caught an error while unpacking', key, "This tends to happen if you forgot to pass a parameter needed for creating an @rest(path, or if RestLink was configured to deeply unpack a path parameter that wasn't provided. This message will only log once per detected instance. Trouble-shooting hint: check @rest(path: and the variables provided to this query.");
                            PathBuilder.warnTable[key] = true;
                        }
                        return '';
                    }
                });
            }
            else {
                chunkActions.push(bit);
                if (!hasBegunQuery && nextIndex >= queryOrigStartIndex) {
                    hasBegunQuery = true;
                    chunkActions.push(true);
                }
            }
            return nextIndex;
        }, 0);
        const result = props => {
            let hasEnteredQuery = false;
            const tmp = chunkActions.reduce((accumulator, action) => {
                if (typeof action === 'string') {
                    return accumulator + action;
                }
                else if (typeof action === 'boolean') {
                    hasEnteredQuery = true;
                    return accumulator;
                }
                else {
                    return accumulator + action(props, hasEnteredQuery);
                }
            }, '');
            return tmp;
        };
        PathBuilder.cache[path] = result;
        return result;
    }
}
/** For accelerating the replacement of paths that are used a lot */
PathBuilder.cache = {};
/** Table to limit the amount of nagging (due to probable API Misuse) we do to once per path per launch */
PathBuilder.warnTable = {};
/** Regexp that finds things that are eligible for variable replacement */
PathBuilder.argReplacement = /({[._a-zA-Z0-9]*})/;
/** Private Helper Function */
function PathBuilderLookupValue(tmp, keyPath) {
    if (keyPath.length === 0) {
        return tmp;
    }
    const remainingKeyPath = [...keyPath]; // Copy before mutating
    const key = remainingKeyPath.shift();
    return PathBuilderLookupValue(tmp[key], remainingKeyPath);
}
/**
 * Some keys should be passed through transparently without normalizing/de-normalizing
 */
const noMangleKeys = ['__typename'];
/** Trivial globalThis polyfill that falls-back to our previous global object in case people had polyfilled that */
const globalScope = (typeof globalThis === 'object' && globalThis) || global;
/** Recursively descends the provided object tree and converts all the keys */
const convertObjectKeys = (object, __converter, keypath = []) => {
    let converter;
    if (__converter.length !== 2) {
        converter = (name, _keypath) => {
            return __converter(name);
        };
    }
    else {
        converter = __converter;
    }
    if (Array.isArray(object)) {
        return object.map((o, index) => convertObjectKeys(o, converter, [...keypath, String(index)]));
    }
    if (isNullish(object) || typeof object !== 'object' || object.constructor !== Object) {
        // Object is a scalar or null / undefined => no keys to convert!
        return object;
    }
    // FileList/File are only available in some browser contexts
    // Notably: *not available* in react-native.
    if ((globalScope.FileList && object instanceof FileList) ||
        (globalScope.File && object instanceof File)) {
        // Object is a FileList or File object => no keys to convert!
        return object;
    }
    return Object.keys(object).reduce((acc, key) => {
        const value = object[key];
        if (noMangleKeys.indexOf(key) !== -1) {
            acc[key] = value;
            return acc;
        }
        const nestedKeyPath = [...keypath, key];
        acc[converter(key, nestedKeyPath)] = convertObjectKeys(value, converter, nestedKeyPath);
        return acc;
    }, {});
};
const noOpNameNormalizer = (name) => {
    return name;
};
/**
 * Helper that makes sure our headers are of the right type to pass to Fetch
 */
export const normalizeHeaders = (headers) => {
    // Make sure that our headers object is of the right type
    if (headers instanceof Headers) {
        return headers;
    }
    else {
        return new Headers((headers || {}));
    }
};
/**
 * Returns a new Headers Group that contains all the headers.
 * - If there are duplicates, they will be in the returned header set multiple times!
 */
export const concatHeadersMergePolicy = (...headerGroups) => {
    return headerGroups.reduce((accumulator, current) => {
        if (!current) {
            return accumulator;
        }
        if (!current.forEach) {
            current = normalizeHeaders(current);
        }
        current.forEach((value, key) => {
            accumulator.append(key, value);
        });
        return accumulator;
    }, new Headers());
};
/**
 * This merge policy deletes any matching headers from the link's default headers.
 * - Pass headersToOverride array & a headers arg to context and this policy will automatically be selected.
 */
export const overrideHeadersMergePolicy = (linkHeaders, headersToOverride, requestHeaders) => {
    const result = new Headers();
    linkHeaders.forEach((value, key) => {
        if (headersToOverride.indexOf(key) !== -1) {
            return;
        }
        result.append(key, value);
    });
    return concatHeadersMergePolicy(result, requestHeaders || new Headers());
};
export const overrideHeadersMergePolicyHelper = overrideHeadersMergePolicy; // Deprecated name
const makeOverrideHeadersMergePolicy = (headersToOverride) => {
    return (linkHeaders, requestHeaders) => {
        return overrideHeadersMergePolicy(linkHeaders, headersToOverride, requestHeaders);
    };
};
const SUPPORTED_HTTP_VERBS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
export const validateRequestMethodForOperationType = (method, operationType) => {
    switch (operationType) {
        case 'query':
            if (SUPPORTED_HTTP_VERBS.indexOf(method.toUpperCase()) !== -1) {
                return;
            }
            throw new Error(`A "query" operation can only support "GET" requests but got "${method}".`);
        case 'mutation':
            if (SUPPORTED_HTTP_VERBS.indexOf(method.toUpperCase()) !== -1) {
                return;
            }
            throw new Error('"mutation" operations do not support that HTTP-verb');
        case 'subscription':
            throw new Error('A "subscription" operation is not supported yet.');
        default: {
            const _exhaustiveCheck = operationType;
            return _exhaustiveCheck;
        }
    }
};
/**
 * Utility to build & throw a JS Error from a "failed" REST-response
 * @param response: HTTP Response object for this request
 * @param result: Promise that will render the body of the response
 * @param message: Human-facing error message
 */
const rethrowServerSideError = (response, result, message) => {
    const error = new Error(message);
    error.response = response;
    error.statusCode = response.status;
    error.result = result;
    throw error;
};
const addTypeToNode = (node, typename) => {
    if (node === null || node === undefined || typeof node !== 'object') {
        return node;
    }
    if (!Array.isArray(node)) {
        node['__typename'] = typename;
        return node;
    }
    return node.map(item => {
        return addTypeToNode(item, typename);
    });
};
const resolver = async (fieldName, root, args, context, info) => {
    const { directives, isLeaf, resultKey } = info;
    const { exportVariablesByNode } = context;
    const exportVariables = exportVariablesByNode.get(root) || {};
    /** creates a copy of this node's export variables for its child nodes. iterates over array results to provide for each child. returns the passed result. */
    const copyExportVariables = (result) => {
        if (result instanceof Array) {
            result.forEach(copyExportVariables);
        }
        else {
            // export variables are stored keyed on the node they are for
            exportVariablesByNode.set(result, { ...exportVariables });
        }
        return result;
    };
    // Support GraphQL Aliases!
    const aliasedNode = (root || {})[resultKey];
    const preAliasingNode = (root || {})[fieldName];
    if (root && directives && directives.export) {
        // @export(as:) is only supported with apollo-link-rest at this time
        // so use the preAliasingNode as we're responsible for implementing aliasing!
        exportVariables[directives.export.as] = preAliasingNode;
    }
    const isATypeCall = directives && directives.type;
    if (!isLeaf && isATypeCall) {
        // @type(name: ) is only supported inside apollo-link-rest at this time
        // so use the preAliasingNode as we're responsible for implementing aliasing!
        // Also: exit early, since @type(name: ) && @rest() can't both exist on the same node.
        if (directives.rest) {
            throw new Error('Invalid use of @type(name: ...) directive on a call that also has @rest(...)');
        }
        // Run the typePatcher too, so a table entry for the @type name still types the node's children
        const typedNode = addTypeNameToResult(addTypeToNode(preAliasingNode, directives.type.name), directives.type.name, context.typePatcher, { resolverParams: { fieldName, root, args, context, info } });
        return copyExportVariables(typedNode);
    }
    const isNotARestCall = !directives || !directives.rest;
    if (isNotARestCall) {
        // This is not tagged with @rest()
        // This might not belong to us so return the aliasNode version preferentially
        return copyExportVariables(aliasedNode || preAliasingNode);
    }
    const { credentials, endpoints, headers, customFetch, operationType, typePatcher, mainDefinition, fragmentDefinitions, fieldNameNormalizer: linkLevelNameNormalizer, fieldNameDenormalizer: linkLevelNameDenormalizer, serializers, responseTransformer, } = context;
    const fragmentMap = createFragmentMap(fragmentDefinitions);
    const { path, endpoint } = directives.rest;
    let { pathBuilder } = directives.rest;
    const endpointOption = getEndpointOptions(endpoints, endpoint);
    const neitherPathsProvided = isNullish(path) && isNullish(pathBuilder);
    if (neitherPathsProvided) {
        throw new Error(`One of ("path" | "pathBuilder") must be set in the @rest() directive. This request had neither, please add one`);
    }
    if (!pathBuilder) {
        // `neitherPathsProvided` already threw when both `path` and `pathBuilder` were nullish,
        // so within this `!pathBuilder` branch `path` is guaranteed to be defined.
        const definedPath = path;
        if (!definedPath.includes(':')) {
            // Colons are the legacy route, and aren't uri encoded anyhow.
            pathBuilder = PathBuilder.replacerForPath(definedPath);
        }
        else {
            console.warn("Deprecated: '@rest(path:' contains a ':' colon, this format will be removed in future versions");
            pathBuilder = ({ args, exportVariables }) => {
                const legacyArgs = {
                    ...args,
                    ...exportVariables,
                };
                const pathWithParams = Object.keys(legacyArgs).reduce((acc, e) => replaceLegacyParam(acc, e, legacyArgs[e]), definedPath);
                if (pathWithParams.includes(':')) {
                    throw new Error('Missing parameters to run query, specify it in the query params or use ' +
                        'an export directive. (If you need to use ":" inside a variable string' +
                        ' make sure to encode the variables properly using `encodeURIComponent' +
                        '`. Alternatively see documentation about using pathBuilder.)');
                }
                return pathWithParams;
            };
        }
    }
    const allParams = {
        args,
        exportVariables,
        context,
        '@rest': directives.rest,
        replacer: pathBuilder,
    };
    const pathWithParams = pathBuilder(allParams);
    let { method, bodyKey, bodyBuilder } = directives.rest;
    const { type, fieldNameDenormalizer: perRequestNameDenormalizer, fieldNameNormalizer: perRequestNameNormalizer, bodySerializer, } = directives.rest;
    if (!method) {
        method = 'GET';
    }
    if (!bodyKey) {
        bodyKey = 'input';
    }
    let body = undefined;
    let overrideHeaders = undefined;
    if (['GET', 'DELETE'].indexOf(method) === -1) {
        // Prepare our body!
        if (!bodyBuilder) {
            // By convention GraphQL recommends mutations having a single argument named "input"
            // https://dev-blog.apollodata.com/designing-graphql-mutations-e09de826ed97
            const maybeBody = allParams.exportVariables[bodyKey] || (allParams.args && allParams.args[bodyKey]);
            if (!maybeBody) {
                throw new Error(`[GraphQL ${method} ${operationType} using a REST call without a body]. No \`${bodyKey}\` was detected. Pass bodyKey, or bodyBuilder to the @rest() directive to resolve this.`);
            }
            bodyBuilder = (_argsWithExport) => {
                return maybeBody;
            };
        }
        body = convertObjectKeys(bodyBuilder(allParams), perRequestNameDenormalizer || linkLevelNameDenormalizer || noOpNameNormalizer);
        let serializedBody;
        if (typeof bodySerializer === 'string') {
            if (!Object.prototype.hasOwnProperty.call(serializers, bodySerializer)) {
                throw new Error('"bodySerializer" must correspond to configured serializer. ' +
                    `Please make sure to specify a serializer called ${bodySerializer} in the "bodySerializers" property of the RestLink.`);
            }
            serializedBody = serializers[bodySerializer](body, headers);
        }
        else {
            serializedBody = bodySerializer
                ? bodySerializer(body, headers)
                : serializers[DEFAULT_SERIALIZER_KEY](body, headers);
        }
        body = serializedBody.body;
        overrideHeaders = new Headers(serializedBody.headers);
    }
    validateRequestMethodForOperationType(method, (operationType || 'query'));
    const requestParams = {
        method,
        headers: overrideHeaders || headers,
        body,
        // Only set credentials if they're non-null as some browsers throw an exception:
        // https://github.com/apollographql/apollo-link-rest/issues/121#issuecomment-396049677
        ...(credentials ? { credentials } : {}),
    };
    const requestUrl = `${endpointOption.uri}${pathWithParams}`;
    const response = await (customFetch || fetch)(requestUrl, requestParams);
    context.responses.push(response);
    let result;
    if (response.ok) {
        if (response.status === 204 || response.headers.get('Content-Length') === '0') {
            // HTTP-204 means "no-content", similarly Content-Length implies the same
            // This commonly occurs when you POST/PUT to the server, and it acknowledges
            // success, but doesn't return your Resource.
            result = {};
        }
        else {
            result = response;
        }
    }
    else if (response.status === 404) {
        // In a GraphQL context a missing resource should be indicated by
        // a null value rather than throwing a network error
        result = null;
    }
    else {
        // Default error handling:
        // Throw a JSError, that will be available under the
        // "Network error" category in apollo-link-error
        let parsed;
        // responses need to be cloned as they can only be read once
        try {
            parsed = await response.clone().json();
        }
        catch (error) {
            // its not json
            parsed = await response.clone().text();
        }
        rethrowServerSideError(response, parsed, `Response not successful: Received status code ${response.status}`);
    }
    const transformer = endpointOption.responseTransformer || responseTransformer;
    if (transformer) {
        // A responseTransformer might call something else than json() on the response.
        try {
            result = await transformer(result, type);
        }
        catch (err) {
            console.warn('An error occurred in a responseTransformer:');
            throw err;
        }
    }
    else if (result && result.json) {
        result = await result.json();
    }
    result = convertObjectKeys(result, perRequestNameNormalizer || linkLevelNameNormalizer || noOpNameNormalizer);
    result = findRestDirectivesThenInsertNullsForOmittedFields(resultKey, result, mainDefinition, fragmentMap, mainDefinition.selectionSet);
    if (isNullish(type)) {
        // No `type` was declared on the @rest(...) directive: synthesise a `__typename` for every
        // object node from the query selection set, so callers don't have to declare a `type` (and a
        // matching `typePatcher`) purely to satisfy Apollo's `__typename` selections.
        result = addAutomaticTypeNames(result, info.field.selectionSet, fragmentMap, [resultKey]);
    }
    else {
        result = addTypeNameToResult(result, type, typePatcher, {
            resolverParams: { fieldName, root, args, context, info },
        });
    }
    return copyExportVariables(result);
};
/**
 * Default key to use when the @rest directive omits the "endpoint" parameter.
 */
const DEFAULT_ENDPOINT_KEY = '';
/**
 * Default key to use when the @rest directive omits the "bodySerializers" parameter.
 */
const DEFAULT_SERIALIZER_KEY = '';
const DEFAULT_JSON_SERIALIZER = (data, headers) => {
    if (!headers.has('content-type')) {
        headers.append('Content-Type', 'application/json');
    }
    return {
        body: JSON.stringify(data),
        headers,
    };
};
const CONNECTION_REMOVE_CONFIG = {
    test: (directive) => directive.name.value === 'rest',
    remove: true,
};
/**
 * RestLink is an apollo-link for communicating with REST services using GraphQL on the client-side
 */
export class RestLink extends ApolloLink {
    constructor({ uri, endpoints, headers, fieldNameNormalizer, fieldNameDenormalizer, typePatcher, customFetch, credentials, bodySerializers, defaultSerializer, responseTransformer, }) {
        super();
        const fallback = {};
        fallback[DEFAULT_ENDPOINT_KEY] = uri || '';
        this.endpoints = Object.assign({}, endpoints || fallback);
        if (isNullish(uri) && isNullish(endpoints)) {
            throw new Error('A RestLink must be initialized with either 1 uri, or a map of keyed-endpoints');
        }
        if (!isNullish(uri)) {
            const currentDefaultURI = (endpoints || {})[DEFAULT_ENDPOINT_KEY];
            if (!isNullish(currentDefaultURI) && currentDefaultURI !== uri) {
                throw new Error("RestLink was configured with a default uri that doesn't match what's passed in to the endpoints map.");
            }
            this.endpoints[DEFAULT_ENDPOINT_KEY] = uri;
        }
        if (isNullish(this.endpoints[DEFAULT_ENDPOINT_KEY])) {
            console.warn('RestLink configured without a default URI. All @rest(…) directives must provide an endpoint key!');
        }
        if (isNullish(typePatcher)) {
            this.typePatcher = (result, __typename, _2) => {
                return { __typename, ...result };
            };
        }
        else if (!Array.isArray(typePatcher) &&
            typeof typePatcher === 'object' &&
            Object.keys(typePatcher)
                .map(key => typePatcher[key])
                .reduce(
            // Make sure all of the values are patcher-functions
            (current, patcher) => current && typeof patcher === 'function', true)) {
            const table = typePatcher;
            this.typePatcher = (data, outerType, patchDeeper, context) => {
                const __typename = data.__typename || outerType;
                if (Array.isArray(data)) {
                    return data.map(d => patchDeeper(d, __typename, patchDeeper, context));
                }
                const subPatcher = table[__typename] || (result => result);
                return {
                    __typename,
                    ...subPatcher(data, __typename, patchDeeper, context),
                };
            };
        }
        else {
            throw new Error('RestLink was configured with a typePatcher of invalid type!');
        }
        if (bodySerializers && Object.prototype.hasOwnProperty.call(bodySerializers, DEFAULT_SERIALIZER_KEY)) {
            console.warn('RestLink was configured to override the default serializer! This may result in unexpected behavior');
        }
        this.responseTransformer = responseTransformer || null;
        this.fieldNameNormalizer = fieldNameNormalizer || null;
        this.fieldNameDenormalizer = fieldNameDenormalizer || null;
        this.headers = normalizeHeaders(headers);
        this.credentials = credentials || null;
        this.customFetch = customFetch;
        this.serializers = {
            [DEFAULT_SERIALIZER_KEY]: defaultSerializer || DEFAULT_JSON_SERIALIZER,
            ...(bodySerializers || {}),
        };
        this.processedDocuments = new Map();
    }
    removeRestSetsFromDocument(query) {
        const cached = this.processedDocuments.get(query);
        if (cached) {
            return cached;
        }
        checkDocument(query);
        const docClone = removeDirectivesFromDocument([CONNECTION_REMOVE_CONFIG], query);
        this.processedDocuments.set(query, docClone);
        return docClone;
    }
    request(operation, forward) {
        const { query, variables } = operation;
        const context = operation.getContext();
        const isRestQuery = hasDirectives(['rest'], query);
        if (!isRestQuery) {
            return forward(operation);
        }
        const nonRest = this.removeRestSetsFromDocument(query);
        // 1. Use the user's merge policy if any
        let headersMergePolicy = context.headersMergePolicy;
        if (isNullish(headersMergePolicy) && Array.isArray(context.headersToOverride)) {
            // 2.a. Override just the passed in headers, if user provided that optional array
            headersMergePolicy = makeOverrideHeadersMergePolicy(context.headersToOverride);
        }
        else if (isNullish(headersMergePolicy)) {
            // 2.b Glue the link (default) headers to the request-context headers
            headersMergePolicy = concatHeadersMergePolicy;
        }
        const headers = headersMergePolicy(this.headers, context.headers);
        if (!headers.has('Accept')) {
            // Since we assume a json body on successful responses set the Accept
            // header accordingly if it is not provided by the user
            headers.append('Accept', 'application/json');
        }
        const credentials = context.credentials || this.credentials;
        const queryWithTypename = addTypenameToDocument(query);
        const mainDefinition = getMainDefinition(query);
        const fragmentDefinitions = getFragmentDefinitions(query);
        const operationType = (mainDefinition || {}).operation || 'query';
        const requestContext = {
            headers,
            endpoints: this.endpoints,
            // Provide an empty map for this request's exports to be stuffed into
            exportVariablesByNode: new Map(),
            credentials,
            customFetch: this.customFetch,
            operationType,
            fieldNameNormalizer: this.fieldNameNormalizer,
            fieldNameDenormalizer: this.fieldNameDenormalizer,
            mainDefinition,
            fragmentDefinitions,
            typePatcher: this.typePatcher,
            serializers: this.serializers,
            responses: [],
            responseTransformer: this.responseTransformer,
        };
        const resolverOptions = {};
        // Uses only the constructor and `subscribe`, which zen-observable and rxjs share. Completes
        // once upstream has completed and every REST resolution it started has emitted.
        return new Observable(observer => {
            let pendingResolutions = 0;
            let upstreamComplete = false;
            const completeWhenSettled = () => {
                if (upstreamComplete && pendingResolutions === 0) {
                    observer.complete();
                }
            };
            const resolveRestFields = ({ data, errors }) => {
                pendingResolutions += 1;
                new Promise(resolve => resolve(graphql(resolver, queryWithTypename, data, requestContext, variables, resolverOptions)))
                    .then(data => {
                    operation.setContext({
                        restResponses: (context.restResponses || []).concat(requestContext.responses),
                    });
                    observer.next({ data, errors });
                    pendingResolutions -= 1;
                    completeWhenSettled();
                })
                    .catch(err => {
                    // An abort means the consumer cancelled the request, so nobody waits for
                    // the stream to settle and it is left open on purpose.
                    if (err.name === 'AbortError') {
                        return;
                    }
                    if (err.result && err.result.errors) {
                        observer.next(err.result);
                    }
                    observer.error(err);
                });
            };
            if (nonRest && forward) {
                operation.query = nonRest;
                const subscription = forward(operation).subscribe({
                    next: resolveRestFields,
                    error: error => observer.error(error),
                    complete: () => {
                        upstreamComplete = true;
                        completeWhenSettled();
                    },
                });
                return () => subscription.unsubscribe();
            }
            upstreamComplete = true;
            resolveRestFields({ data: {} });
            return undefined;
        });
    }
}
